/**
 * "Ask the Library" on `/research`. Its conversations are practice-scoped
 * Casey threads of kind `library`: listed only here, never in the practice
 * Casey's history. Asking from a fresh composer starts a new conversation;
 * the header lists past ones to reopen. Every agent message
 * carries `metadata.research_chat` and the picked library sources
 * (`metadata.research_sources`: Case Law for a new conversation, the last
 * picks when one is reopened; a send waits for at least one), so the
 * backend loads those sources' immigration-library tools and requires a call
 * to each before Casey answers. Each answer lists the library documents it
 * rests on, opening in the Research reader; the model and speed picker and
 * the model line stay out of this chat.
 */
import { Library, SquarePen } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC
} from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AgentComposerBar } from "@/features/agents/components/AgentComposerBar";
import { AgentTranscript } from "@/features/agents/components/AgentTranscript";
import type { AgentScope, AgentSpec } from "@/features/agents/types";
import type { AgentThread } from "@/features/agents/api/agentApi";
import type {
  AgentMessageMetadata,
  AgentUIMessage
} from "@/features/agents/useAgentChat";
import { useAgentSpecs } from "@/features/agents/useAgentSpecs";
import { useScopedAgentChat } from "@/features/agents/useScopedAgentChat";
import { ThreadSwitcher } from "@/features/caseIde/chat/components/ThreadSwitcher";
import { cn } from "@/lib/utils";

import { RESEARCH_COLUMN } from "../layout";
import { researchPath } from "../routes";
import { RESEARCH_SOURCE_IDS } from "../sources";
import type { ResearchSourceId } from "../types";
import { LibrarySources } from "./LibrarySources";

export const CHAT_TITLE = "Ask the Library";
export const CHAT_PLACEHOLDER = "Ask the library";
export const CHAT_DISCLAIMER =
  "Answers come from the immigration library. Please verify them.";

/** What every message from this chat carries: the flag and its sources. */
export const libraryChatMetadata = (
  sources: readonly ResearchSourceId[]
): AgentMessageMetadata => ({
  research_chat: true,
  research_sources: [...sources]
});

/** A new conversation researches case law until the attorney picks more. */
export const DEFAULT_CHAT_SOURCES: readonly ResearchSourceId[] = ["case_law"];

/** The sources the conversation's latest question asked for, if it named any. */
export const lastPickedSources = (
  messages: readonly AgentUIMessage[]
): ResearchSourceId[] | null => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const { role, metadata } = messages[i];
    const ids = role === "user" ? metadata?.research_sources : undefined;
    if (ids?.length) {
      const known = RESEARCH_SOURCE_IDS.filter((id) => ids.includes(id));
      if (known.length) return known;
    }
  }
  return null;
};

/** This page's thread, separate from the practice Casey the panel remembers. */
export const researchThreadKey = (practiceId: string): string =>
  `research.library.${practiceId}`;

/**
 * `card`: the landing's block, before a conversation. `pane`: the open
 * conversation, filling its column, its content on the page's column.
 */
export type ResearchChatVariant = "card" | "pane";

const frame = (variant: ResearchChatVariant) =>
  variant === "card"
    ? "flex flex-col overflow-hidden rounded-xl bg-elevated ring-1 ring-line-2"
    : "flex min-h-0 flex-col bg-surface";
const inset = (variant: ResearchChatVariant) =>
  variant === "card" ? "px-4" : RESEARCH_COLUMN;

type Conversations = {
  threads: AgentThread[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  disabled: boolean;
  /** A conversation is open (the New Conversation action has something to leave). */
  open: boolean;
};

const ChatHeader: FC<{
  variant: ResearchChatVariant;
  conversations?: Conversations;
}> = ({ variant, conversations }) => {
  const active = conversations?.open
    ? conversations.threads.find((t) => t.id === conversations.activeId)
    : undefined;
  return (
    <header
      className={cn(
        "flex h-12 shrink-0 items-center gap-2 border-b",
        variant === "card" ? "border-line-1" : "border-line-2",
        inset(variant)
      )}
    >
      <Library className="size-4 shrink-0 text-fg-3" />
      <h2
        className="shrink-0 text-md font-semibold text-fg"
        data-testid="research-chat-title"
      >
        {CHAT_TITLE}
      </h2>
      <span
        className="min-w-0 flex-1 truncate text-sm text-fg-3"
        data-testid="research-chat-thread-title"
      >
        {active?.title ?? ""}
      </span>
      {conversations ? (
        <>
          <ThreadSwitcher
            compact
            triggerSize="icon-xs"
            threads={conversations.threads}
            activeId={conversations.activeId}
            onSelect={conversations.onSelect}
            onNew={conversations.onNew}
            onDelete={conversations.onDelete}
            disabled={conversations.disabled}
          />
          <Button
            variant="ghost"
            size="icon-xs"
            className="text-fg-3"
            aria-label="New Conversation"
            title="New Conversation"
            onClick={conversations.onNew}
            disabled={!conversations.open}
            data-testid="research-chat-new"
          >
            <SquarePen />
          </Button>
        </>
      ) : null}
    </header>
  );
};

type LiveProps = {
  className?: string;
  variant: ResearchChatVariant;
  scope: AgentScope;
  spec: AgentSpec | null;
  onOpenDocument: (id: string) => void;
  onConversingChange?: (conversing: boolean) => void;
};

const ResearchChatLive: FC<LiveProps> = ({
  className,
  variant,
  scope,
  spec,
  onOpenDocument,
  onConversingChange
}) => {
  const [sources, setSources] = useState<ResearchSourceId[]>(() => [
    ...DEFAULT_CHAT_SOURCES
  ]);
  const pickSources = useCallback(
    (ids: ResearchSourceId[]) => setSources(ids),
    []
  );
  const messageMetadata = useMemo(
    () => libraryChatMetadata(sources),
    [sources]
  );
  const researchPicker = useMemo(
    () => ({ value: sources, onChange: pickSources }),
    [sources, pickSources]
  );
  const scoped = useScopedAgentChat({
    agent: "practice",
    scope,
    spec,
    owner: "research-library",
    threadKey: researchThreadKey(scope.id),
    threadKind: "library",
    messageMetadata
  });
  const { chat } = scoped;
  const conversing = !scoped.empty;
  // A new conversation starts on the default; a reopened one on its last
  // picks, read once its transcript is restored.
  const pickedFor = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (chat.restoring || pickedFor.current === chat.threadId) return;
    if (!chat.threadId) {
      pickedFor.current = null;
      setSources([...DEFAULT_CHAT_SOURCES]);
      return;
    }
    if (!chat.messages.length) return;
    pickedFor.current = chat.threadId;
    const last = lastPickedSources(chat.messages);
    if (last) setSources(last);
  }, [chat.messages, chat.restoring, chat.threadId]);
  useEffect(() => {
    onConversingChange?.(conversing);
  }, [conversing, onConversingChange]);
  const threads = useMemo(
    () => chat.threads.filter((t) => !t.archived),
    [chat.threads]
  );
  const replyAppendix = useCallback(
    (message: AgentUIMessage) => (
      <LibrarySources message={message} onOpen={onOpenDocument} />
    ),
    [onOpenDocument]
  );

  return (
    <section
      className={cn(frame(variant), className)}
      data-testid="research-chat"
      data-variant={variant}
      aria-label={CHAT_TITLE}
    >
      <ChatHeader
        variant={variant}
        conversations={{
          threads,
          activeId: chat.threadId,
          onSelect: chat.setThreadId,
          onNew: chat.startNewThread,
          onDelete: (id) => {
            const wasOpen = id === chat.threadId;
            void chat.deleteThread(id).then(() => {
              if (wasOpen) chat.startNewThread();
            });
          },
          disabled: chat.threadsLoading,
          open: conversing
        }}
      />
      {scoped.empty ? (
        <div
          className={cn(
            inset(variant),
            variant === "card"
              ? "pt-4 pb-3"
              : "flex min-h-0 flex-1 flex-col justify-end pb-3"
          )}
        >
          <p className="text-md text-fg-2">
            Casey searches the sources you pick and cites the documents it used.
          </p>
        </div>
      ) : (
        <AgentTranscript
          scoped={scoped}
          className="min-h-0 flex-1"
          contentClassName={RESEARCH_COLUMN}
          hideModel
          replyAppendix={replyAppendix}
        />
      )}
      <AgentComposerBar
        scoped={scoped}
        researchPicker={researchPicker}
        showModeSelect={false}
        showModelSelect={false}
        draftKey={`research-library:${scope.id}`}
        placeholder={CHAT_PLACEHOLDER}
        hideDisclaimer
        autoFocus={false}
        className={cn(inset(variant), "pb-1")}
      />
      <p className="shrink-0 pb-3 text-center text-sm text-fg-3">
        {CHAT_DISCLAIMER}
      </p>
    </section>
  );
};

type Props = {
  className?: string;
  variant?: ResearchChatVariant;
  /** Opens a cited document in the reader. Defaults to `/research?doc=`. */
  onOpenDocument?: (id: string) => void;
  /** Whether a conversation is open, so the page can give it the column. */
  onConversingChange?: (conversing: boolean) => void;
};

/**
 * Casey's composer and transcript, titled for the library. Sending from a
 * fresh composer opens a new conversation in this pane (the same streaming
 * transcript and tool cards). The search box above stays put.
 */
export const ResearchChat: FC<Props> = ({
  className,
  variant = "pane",
  onOpenDocument,
  onConversingChange
}) => {
  const { practiceScope, specFor, loading } = useAgentSpecs();
  const navigate = useNavigate();
  const openDocument = useCallback(
    (id: string) =>
      onOpenDocument
        ? onOpenDocument(id)
        : navigate(researchPath({ docId: id })),
    [navigate, onOpenDocument]
  );
  if (!practiceScope) {
    return (
      <section
        className={cn(frame(variant), className)}
        data-testid="research-chat"
        data-variant={variant}
        aria-label={CHAT_TITLE}
      >
        <ChatHeader variant={variant} />
        <div
          className={cn(
            inset(variant),
            "flex min-h-0 flex-1 flex-col justify-end py-4"
          )}
        >
          {loading ? (
            <Skeleton className="h-10 w-full rounded-xl" />
          ) : (
            <p className="text-center text-sm text-fg-3">
              The library assistant is unavailable.
            </p>
          )}
        </div>
      </section>
    );
  }
  return (
    <ResearchChatLive
      className={className}
      variant={variant}
      scope={practiceScope}
      spec={specFor("assistant")}
      onOpenDocument={openDocument}
      onConversingChange={onConversingChange}
    />
  );
};
